import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FaFacebookF, FaInstagram, FaLinkedin, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { Link } from "react-router-dom";

const Footer: FC = () => {
  const { t, i18n } = useTranslation();
  const isMk = i18n.language?.startsWith("mk");

  const navLinks = [
    { to: "/about", label: t("navbar.aboutUs") },
    { to: "/history", label: t("navbar.history") },
    { to: "/careers", label: t("navbar.careers") },
    { to: "/contacts", label: t("navbar.contacts") },
  ];

  const eyebrow = "text-xs font-bold uppercase tracking-[0.18em] text-brand-600 mb-5";

  return (
    <footer className="bg-brand-50 border-t-4 border-brand-400">
      {/* Main area */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid gap-10 lg:gap-12 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <img
              src={isMk ? "/LOGO MK POSLEDNO.png" : "/LOGO GB POSLEDNO.png"}
              alt="Galafarm"
              className="h-12 w-auto"
            />
            <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-gray-500">
              {t("footer.description")}
            </p>
            <span className="mt-7 block h-1 w-16 rounded-full bg-brand-400" />
          </div>

          {/* Company links */}
          <nav className="lg:col-span-2" aria-label={t("footer.company")}>
            <h3 className={eyebrow}>{t("footer.company")}</h3>
            <ul className="space-y-3 text-[15px] text-gray-500">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="inline-block transition-all duration-200 hover:translate-x-1 hover:text-brand-600"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h3 className={eyebrow}>{t("footer.contact")}</h3>
            <ul className="space-y-4 text-[15px] text-gray-500">
              <li className="flex items-start gap-3">
                <FaMapMarkerAlt className="mt-1 shrink-0 text-brand-500" />
                <span>
                  {t("footer.address")}
                  <br />
                  {t("footer.postalCode")}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <FaPhoneAlt className="shrink-0 text-brand-500" />
                <a href="tel:+38922050450" className="transition-colors duration-200 hover:text-brand-600">
                  +389 2 20 50 450
                </a>
              </li>
              <li className="flex items-center gap-3">
                <FaEnvelope className="shrink-0 text-brand-500" />
                <a
                  href="mailto:info@galafarm.com.mk"
                  className="transition-colors duration-200 hover:text-brand-600"
                >
                  info@galafarm.com.mk
                </a>
              </li>
            </ul>
          </div>

          {/* Map */}
          <div className="lg:col-span-3">
            <h3 className={eyebrow}>{t("footer.location")}</h3>
            <div className="h-44 w-full overflow-hidden rounded-xl border border-brand-200 shadow-sm">
              <iframe
                title="Galafarm Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2252.0839247074928!2d21.338289454547546!3d42.03014554925974!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135413071091d5b7%3A0x85d4ec382782278d!2sGalafarm!5e1!3m2!1sen!2smk!4v1744886416954!5m2!1sen!2smk"
                width="100%"
                height="100%"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-full w-full"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar — stays within the light botanical palette */}
      <div className="border-t border-brand-200 bg-brand-100/50">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-between gap-4 px-6 py-5 sm:flex-row">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} {t("footer.bottomText")}
          </p>
          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/galafarm.com.mk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-300 text-brand-600 transition-colors duration-200 hover:border-brand-400 hover:bg-brand-400 hover:text-white"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.instagram.com/galafarm.mk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-300 text-brand-600 transition-colors duration-200 hover:border-brand-400 hover:bg-brand-400 hover:text-white"
            >
              <FaInstagram />
            </a>
            <a
              href="https://www.linkedin.com/company/76544278/admin/dashboard/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-300 text-brand-600 transition-colors duration-200 hover:border-brand-400 hover:bg-brand-400 hover:text-white"
            >
              <FaLinkedin />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
